import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Mail, X, StickyNote, Send, Home } from "lucide-react";
import { useAdminAuth } from "../hooks/useAdminAuth";
import { AdminNav } from "../components/admin/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLUMNS = [
  { key: "novo", label: "Novo", dot: "#00D765" },
  { key: "em_atendimento", label: "Em atendimento", dot: "#1AA886" },
  { key: "visita_agendada", label: "Visita agendada", dot: "#004C3F" },
  { key: "negociacao", label: "Negociação", dot: "#C9A227" },
  { key: "ganho", label: "Ganho", dot: "#2F855A" },
  { key: "perdido", label: "Perdido", dot: "#9CA3AF" },
];

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }) + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
};

export default function AdminCrm() {
  const { user, headers, logout } = useAdminAuth();
  const [leads, setLeads] = useState(null);
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/crm/leads`, { headers: headers() });
      setLeads(r.data);
    } catch {
      setLeads([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return;
    load();
    axios
      .get(`${API}/users`, { headers: headers() })
      .then((r) => setUsers(r.data.filter((u) => u.active !== false)))
      .catch(() => setUsers([]));
    axios
      .get(`${API}/properties`)
      .then((r) => setProperties(r.data))
      .catch(() => setProperties([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const moveLead = async (leadId, status) => {
    const prev = leads;
    setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, status } : l)));
    try {
      await axios.put(`${API}/crm/leads/${leadId}`, { status }, { headers: headers() });
    } catch {
      setLeads(prev);
      toast.error("Não foi possível mover o contato.");
    }
  };

  const updateLead = async (leadId, field, value) => {
    try {
      await axios.put(`${API}/crm/leads/${leadId}`, { [field]: value }, { headers: headers() });
      setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, [field]: value || null } : l)));
      setSelected((s) => (s ? { ...s, [field]: value || null } : s));
      toast.success("Contato atualizado.");
    } catch {
      toast.error("Não foi possível atualizar.");
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      const r = await axios.post(`${API}/crm/leads/${selected.id}/notes`, { text: note.trim() }, { headers: headers() });
      const newNote = r.data;
      setLeads((ls) => ls.map((l) => (l.id === selected.id ? { ...l, notes: [...(l.notes || []), newNote] } : l)));
      setSelected((s) => ({ ...s, notes: [...(s.notes || []), newNote] }));
      setNote("");
    } catch {
      toast.error("Não foi possível salvar a anotação.");
    } finally {
      setSavingNote(false);
    }
  };

  const userName = (email) => users.find((u) => u.email === email)?.name || email;
  const propTitle = (id) => properties.find((p) => p.id === id)?.title || null;

  return (
    <main className="mx-auto min-h-screen max-w-[1600px] px-6 pt-20 pb-24 lg:px-10" data-testid="admin-crm">
      <AdminNav user={user} onLogout={logout} />
      <div className="mt-8 mb-10">
        <p className="text-sm font-bold uppercase tracking-[0.25em] text-seed-salad">CRM</p>
        <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter text-seed-deep" data-testid="crm-title">
          Funil de contatos
        </h1>
        <p className="mt-2 text-sm text-seed-deep/50">Arraste os contatos entre as etapas. Clique para ver detalhes, atribuir responsável e anotar.</p>
      </div>

      {leads === null ? (
        <div className="flex justify-center py-16">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-seed-deep/10 border-t-seed-salad" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6" data-testid="crm-board">
          {COLUMNS.map((col) => {
            const colLeads = leads.filter((l) => (l.status || "novo") === col.key);
            return (
              <div
                key={col.key}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  moveLead(e.dataTransfer.getData("text/plain"), col.key);
                }}
                className="min-h-[300px] rounded-lg bg-white/60 p-3"
                data-testid={`crm-col-${col.key}`}
              >
                <div className="mb-3 flex items-center justify-between px-1">
                  <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-seed-deep">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: col.dot }} />
                    {col.label}
                  </span>
                  <span className="rounded-full bg-seed-deep/5 px-2 py-0.5 text-xs font-bold text-seed-deep/60">{colLeads.length}</span>
                </div>
                <div className="space-y-3">
                  {colLeads.map((l) => (
                    <div
                      key={l.id}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData("text/plain", l.id)}
                      onClick={() => setSelected(l)}
                      data-testid={`crm-card-${l.id.slice(0, 8)}`}
                      className="cursor-grab rounded-md bg-white p-4 shadow-sm transition-shadow duration-300 hover:shadow-md active:cursor-grabbing"
                    >
                      <p className="font-heading text-sm font-bold text-seed-deep">{l.nome}</p>
                      <p className="mt-1 flex items-center gap-1.5 text-xs text-seed-deep/60">
                        <Phone size={11} className="text-seed-salad" /> {l.telefone}
                      </p>
                      {propTitle(l.property_id) && (
                        <p className="mt-1 flex items-center gap-1.5 truncate text-xs font-semibold text-seed-deep/70">
                          <Home size={11} className="shrink-0 text-seed-salad" /> {propTitle(l.property_id)}
                        </p>
                      )}
                      {l.interesse && (
                        <span className="mt-2 inline-block rounded-full bg-seed-salad/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-seed-salad">
                          {l.interesse}
                        </span>
                      )}
                      <div className="mt-3 flex items-center justify-between text-[10px] font-semibold text-seed-deep/40">
                        <span>{fmtDate(l.created_at)}</span>
                        {l.assigned_to && <span className="truncate pl-2 text-seed-deep/60">{userName(l.assigned_to)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {selected && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelected(null)}
              className="fixed inset-0 z-[60] bg-seed-deep/40 backdrop-blur-sm"
              data-testid="crm-drawer-overlay"
            />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="fixed right-0 top-0 z-[70] h-full w-full max-w-md overflow-y-auto bg-white p-8 shadow-2xl"
              data-testid="crm-drawer"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-heading text-2xl font-black tracking-tight text-seed-deep" data-testid="crm-drawer-name">{selected.nome}</h2>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-widest text-seed-deep/40">Recebido em {fmtDate(selected.created_at)}</p>
                </div>
                <button onClick={() => setSelected(null)} data-testid="crm-drawer-close" className="text-seed-deep/40 hover:text-seed-deep">
                  <X size={22} />
                </button>
              </div>

              <div className="mt-6 space-y-3 text-sm">
                <a href={`https://wa.me/55${(selected.telefone || "").replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 font-semibold text-seed-deep transition-colors hover:text-seed-salad" data-testid="crm-drawer-phone">
                  <Phone size={15} className="text-seed-salad" /> {selected.telefone}
                </a>
                {selected.email && (
                  <p className="flex items-center gap-2 text-seed-deep/70">
                    <Mail size={15} className="text-seed-salad" /> {selected.email}
                  </p>
                )}
                {selected.interesse && <p className="text-seed-deep/70"><strong>Interesse:</strong> {selected.interesse}</p>}
                <p className="text-seed-deep/70" data-testid="crm-drawer-origem"><strong>Origem:</strong> {selected.origem || "site"}</p>
                {selected.mensagem && <p className="rounded-md bg-seed-off p-4 leading-relaxed text-seed-deep/70" data-testid="crm-drawer-message">{selected.mensagem}</p>}
              </div>

              <div className="mt-8">
                <label className="text-xs font-bold uppercase tracking-widest text-seed-deep/50">Responsável</label>
                <select
                  value={selected.assigned_to || ""}
                  onChange={(e) => updateLead(selected.id, "assigned_to", e.target.value)}
                  className="mt-2 h-12 w-full rounded-md bg-seed-off px-4 text-sm text-seed-deep outline-none focus:shadow-[0_0_0_2px_#00D765]"
                  data-testid="crm-assign-select"
                >
                  <option value="">Sem responsável</option>
                  {users.map((u) => (
                    <option key={u.email} value={u.email}>{u.name || u.email}</option>
                  ))}
                </select>
              </div>

              <div className="mt-6">
                <label className="text-xs font-bold uppercase tracking-widest text-seed-deep/50">Imóvel de interesse</label>
                <select
                  value={selected.property_id || ""}
                  onChange={(e) => updateLead(selected.id, "property_id", e.target.value)}
                  className="mt-2 h-12 w-full rounded-md bg-seed-off px-4 text-sm text-seed-deep outline-none focus:shadow-[0_0_0_2px_#00D765]"
                  data-testid="crm-property-select"
                >
                  <option value="">Nenhum vinculado</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>{p.title} · {p.price}</option>
                  ))}
                </select>
              </div>

              <div className="mt-8">
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-seed-deep/50">
                  <StickyNote size={14} /> Anotações
                </p>
                <div className="mt-3 space-y-3" data-testid="crm-notes-list">
                  {(selected.notes || []).length === 0 && <p className="text-sm text-seed-deep/40">Nenhuma anotação ainda.</p>}
                  {(selected.notes || []).map((n) => (
                    <div key={n.id} className="rounded-md bg-seed-off p-4">
                      <p className="text-sm leading-relaxed text-seed-deep/80">{n.text}</p>
                      <p className="mt-2 text-[10px] font-bold uppercase tracking-wider text-seed-deep/40">
                        {n.author} · {fmtDate(n.created_at)}
                      </p>
                    </div>
                  ))}
                </div>
                <form onSubmit={addNote} className="mt-4 flex gap-2">
                  <input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Ex.: ligou dia 20, quer casa até 600 mil"
                    className="h-12 flex-1 rounded-md bg-seed-off px-4 text-sm text-seed-deep outline-none placeholder:text-seed-deep/40 focus:shadow-[0_0_0_2px_#00D765]"
                    data-testid="crm-note-input"
                  />
                  <button
                    type="submit"
                    disabled={savingNote}
                    data-testid="crm-note-submit"
                    className="flex h-12 w-12 items-center justify-center rounded-md bg-seed-deep text-seed-off transition-transform hover:scale-105 disabled:opacity-60"
                    aria-label="Adicionar anotação"
                  >
                    <Send size={16} />
                  </button>
                </form>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </main>
  );
}
